import type { TCatFieldName, TCatFormDataInput } from "@./suf-store";

/**
 * Safely retrieves the value of a nested field from an object using dot notation.
 *
 * @param obj - The source cat object (schema `TCatFormDataInput` shape).
 * @param path - A dot-separated string (`TCatFieldName`) representing the path to the nested field
 *               (e.g. `"name"` or `"age.years"`).
 * @returns The value at the specified path, or `undefined` if the path is invalid.
 */
export const getNestedFieldValue = (
  obj: TCatFormDataInput,
  path: TCatFieldName,
): unknown => path.split(".").reduce((acc: unknown, key) => (
  acc && typeof acc === "object" && acc !== null
    ? (acc as Record<string, unknown>)[key]
    : undefined
), obj);

/**
 * Returns a new object with the specified nested field removed.
 *
 * @param obj - The original cat object to remove the field from.
 * @param path - A dot-separated string (`TCatFieldName`)representing the path to the field to remove
 *               (e.g. `"age.years"` or `"name"`).
 * @returns A new cat object with the field removed.
 */
export const removeNestedField = (obj: TCatFormDataInput, path: TCatFieldName): TCatFormDataInput => {
  const keys = path.split(".");
  const [ firstKey, ...restKeys ] = keys;

  if (restKeys.length === 0) {
    const { [firstKey]: removed, ...rest } = obj as Record<string, unknown>;
    return rest as TCatFormDataInput;
  }

  const nested = removeNestedField(
    ((obj as Record<string, unknown>)[firstKey] ?? {}) as TCatFormDataInput,
    restKeys.join(".") as TCatFieldName,
  );

  return { ...obj, [firstKey]: nested } as TCatFormDataInput;
};

/**
 * Updates a nested field within a cat object based on a dot-notated path.
 *
 * @param obj - The original cat object to update.
 * @param path - The dot-notated (`TCatFieldName`) path to the field to update (e.g., "diet.current.brand").
 * @param value - The new value to set at the specified nested path.
 * @returns A new cat object with the updated value, leaving the original object unchanged.
 *
 * @example
 * updateNestedField(cat, "diet.current.brand", "Whiskas");
 * // Returns a new object where cat.diet.current.brand === "Whiskas"
 */
export const updateNestedField = (
  obj: TCatFormDataInput,
  path: TCatFieldName,
  value: unknown,
): TCatFormDataInput => {
  const keys = path.split(".");
  const [ firstKey, ...restKeys ] = keys;

  if (restKeys.length === 0) {
    return { ...obj, [firstKey]: value } as TCatFormDataInput;
  }

  const nested = updateNestedField(
    ((obj as Record<string, unknown>)[firstKey] ?? {}) as TCatFormDataInput,
    restKeys.join(".") as TCatFieldName,
    value,
  );

  return { ...obj, [firstKey]: nested } as TCatFormDataInput;
};
